import { getValidateOptions } from '@services/vehiclequotationservice';
import { updateOptions } from '@store/reducers/selectedoptions';
import {
  updateAddedOptions,
  updateSelectedOptions,
} from '@store/reducers/vehiclesearchparams';
import { ValidateOption } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/validateoption';
import { VehicleOptionItem } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/vehicleoptionitem';
import { AvailableOption } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/availableoption';
import { OptionType } from '../enums';
import { CustomisationOption } from 'interface';
import { useAppDispatch, useAppSelector } from './statehooks';
import { usePricing } from './usepricing';
import { useFinalisedOptions } from '@hooks/usefinalisedoptions';

export const useSelectedOptions = () => {
  const dispatch = useAppDispatch();
  const { updatePricingWithOptions } = usePricing();
  const { colours, options } = useAppSelector((state) => state.selectedOptions);
  const vehicleSearchParams = useAppSelector(
    (state) => state.vehicleSearchParams
  );

  const { pricingType } = useAppSelector((state) => state.vehicleInformation);
  const finalisedOptions = useFinalisedOptions(colours, options);
  const selectedOptions = finalisedOptions.finalisedOptions;

  const getSelectedOptionIds = (optionsUpdated?: CustomisationOption[]) => {
    const selectedOptionIds: number[] = [];
    const selectedPaint = colours.paint.find((f) => f.checked);
    const selectedTrim = colours.trim.find((f) => f.checked);
    const selectedHood = colours.hood.find((f) => f.checked);

    if (selectedPaint) {
      selectedOptionIds.push(selectedPaint.id);
    }

    if (selectedTrim) {
      selectedOptionIds.push(selectedTrim.id);
    }

    if (selectedHood) {
      selectedOptionIds.push(selectedHood.id);
    }

    options.forEach((p) =>
      p.items.forEach((e) => {
        if (e.checked) {
          selectedOptionIds.push(e.id);
        }
      })
    );

    if (!optionsUpdated) {
      return selectedOptionIds;
    }

    optionsUpdated.forEach((e) => {
      if (e.checked) {
        selectedOptionIds.push(e.optionId);
      } else {
        const indexToRemove = selectedOptionIds.indexOf(e.optionId);
        selectedOptionIds.splice(indexToRemove, 1);
      }
    });

    return selectedOptionIds;
  };

  const updateSelectedOptionIds = (
    optionsToUpdate: CustomisationOption[],
    optionId: number
  ) => {
    if (optionsToUpdate.length === 0) {
      return;
    }

    const selectedOptionIds = getSelectedOptionIds(optionsToUpdate);
    const addedOptionId = selectedOptionIds ? selectedOptionIds[0] : null;

    dispatch(updateOptions(optionsToUpdate));
    updatePricingWithOptions(selectedOptionIds, addedOptionId, pricingType);
    dispatch(updateSelectedOptions(selectedOptionIds));
    dispatch(updateAddedOptions(optionId));
  };

  //check the option to remove is not a dependant of a selected parent
  //can remove if it is a dependant but its parent is also its dependant (linked both ways)
  const canRemove = (option: VehicleOptionItem, parentOptionId?: number) => {
    if (option.disabled) {
      return false;
    }

    return !options.find((f) =>
      f.items.find(
        (i) =>
          (i.id !== parentOptionId || !parentOptionId) &&
          i.checked &&
          i.optionRules.find((d) => d.id === option.id) &&
          !(option.optionRules && option.optionRules.find((d) => d.id === i.id))
      )
    );
  };

  const getColourOptionForUpdate = (
    colourOptions: AvailableOption[],
    optionId: number,
    optionType: OptionType,
    checked: boolean
  ) => {
    let option: CustomisationOption = {
      optionId: optionId,
      checked: checked,
    };

    const found = colourOptions.find((f) => f.id === optionId);

    if (found) {
      option.optionType = optionType;
      return option;
    }

    return null;
  };

  const getColourOptionToUpdate = (optionId: number, checked: boolean) => {
    const paintOption = getColourOptionForUpdate(
      colours.paint,
      optionId,
      OptionType.paint,
      checked
    );

    if (paintOption) {
      return paintOption;
    }

    const trimOption = getColourOptionForUpdate(
      colours.trim,
      optionId,
      OptionType.trim,
      checked
    );

    if (trimOption) {
      return trimOption;
    }

    const hoodOption = getColourOptionForUpdate(
      colours.hood,
      optionId,
      OptionType.hood,
      checked
    );

    if (hoodOption) {
      return hoodOption;
    }

    return null;
  };

  const getOptionsToUpdate = (optionId: number, checked: boolean) => {
    const colourOption = getColourOptionToUpdate(optionId, checked);

    if (colourOption) {
      return [colourOption];
    }

    let option: CustomisationOption = {
      optionId: optionId,
      checked: checked,
    };

    const optionType = options.find((f) =>
      f.items.find((i) => i.id === optionId)
    );

    option.optionType = OptionType.option;
    option.title = optionType?.name;

    const foundOption = optionType?.items.find((f) => f.id === optionId);

    if (!checked && foundOption && !canRemove(foundOption)) {
      return [];
    }

    if (!foundOption?.optionRules || foundOption.optionRules.length === 0) {
      return [option];
    }

    const foundOptions: CustomisationOption[] = [option];
    foundOption.optionRules.forEach((e) => {
      if (checked || (!checked && canRemove(e, optionId))) {
        foundOptions.push({
          optionId: e.id,
          checked: checked,
          optionType: OptionType.option,
          title: options.find((f) => f.items.find((i) => i.id === e.id))?.name,
        });
      }
    });

    return foundOptions;
  };

  const unselectOption = (optionId: number) => {
    const optionsToUpdate = getOptionsToUpdate(optionId, false);
    updateSelectedOptionIds(optionsToUpdate, optionId);
  };

  const selectOption = (optionId: number) => {
    getValidateOptions(
      vehicleSearchParams.id,
      vehicleSearchParams.duration,
      vehicleSearchParams.include_maintenance,
      vehicleSearchParams.initial_payment,
      vehicleSearchParams.yearly_mileage,
      optionId,
      getSelectedOptionIds()
    ).subscribe({
      next: (result: ValidateOption) => {
        const optionsToUpdate: CustomisationOption[] = [];
        // need to uncheck the conflicting options and set the new option as checked
        // uncheck any conflicting options and set the new option as selected
        if (result.conflictingOptions && result.conflictingOptions.length > 0) {
          //need to check if the conflicting option returned is the one being added,
          //if it is, it should not select it and leave the current selected as they are
          if (result.conflictingOptions.some((x) => x === optionId)) {
            return;
          } else {
            result.conflictingOptions.map((m) => {
              getOptionsToUpdate(m, false).map((i) => optionsToUpdate.push(i));
            });
          }
        }

        getOptionsToUpdate(optionId, true).map((m) => optionsToUpdate.push(m));

        updateSelectedOptionIds(optionsToUpdate, optionId);
      },
    });
  };

  const getColourOptionChecked = (colourOptions: AvailableOption[]) => {
    return colourOptions.find((f) => f.id)?.checked;
  };

  const isOptionSelected = (
    optionId: number,
    title: string,
    optionType: OptionType
  ) => {
    switch (optionType) {
      case OptionType.paint:
        return getColourOptionChecked(colours.paint);
      case OptionType.trim:
        return getColourOptionChecked(colours.trim);
      case OptionType.hood:
        return getColourOptionChecked(colours.hood);
      case OptionType.option:
        return options
          .find((f) => f.name === title)
          ?.items.find((f) => f.id === optionId)?.checked;
    }
  };

  const getNumberOfExtrasSelected = () => {
    var coloursSelected = colours.paint
      .concat(colours.hood, colours.trim)
      .flat()
      .filter((x) => x.checked);
    var optionsSelected = options
      .map((o) => o.items)
      .flat()
      .filter((x) => x.checked);
    var coloursCount = coloursSelected.length;
    var optionsCount = optionsSelected.length;
    return coloursCount + optionsCount;
  };

  const getTotalCostOfExtrasSelected = () => {
    var coloursSelected = colours.paint
      .concat(colours.hood, colours.trim)
      .flat()
      .filter((x) => x.checked);
    var optionsSelected = options
      .map((o) => o.items)
      .flat()
      .filter((x) => x.checked);
    var coloursTotalCost = coloursSelected
      .map((c) => parseFloat(c.price.replace('£', '').replace(',', '')))
      .reduce((a, b) => {
        return a + b;
      }, 0);
    var optionsTotalCost = optionsSelected
      .map((i) => parseFloat(i.price.replace('£', '').replace(',', '')))
      .reduce((a, b) => {
        return a + b;
      }, 0);
    return coloursTotalCost + optionsTotalCost;
  };

  return {
    selectedOptions,
    unselectOption,
    selectOption,
    isOptionSelected,
    getNumberOfExtrasSelected,
    getTotalCostOfExtrasSelected,
  };
};
